// The lesson result screen's decisions, shared by the web and the phone.
import { itemById, units, unitKeys } from "@aybuben/core/content/course.ts";
import { itemIdOf, type ProgressKey } from "@aybuben/core/content/types.ts";
import { trainedKeys } from "@aybuben/core/progress/query.ts";
import type { LessonSummary } from "@aybuben/core/progress/reduce.ts";
import type { TreeMoment } from "@aybuben/core/progress/tree.ts";
import type { GameState } from "@aybuben/core/state.ts";

import type { UiKey } from "../i18n/en.ts";
import type { CueKind } from "../lesson/cues.ts";

/** When the tree's cue sounds: as the growth animation lands, after the lesson's own chime. */
export const TREE_CUE_MS = 1300;

export interface ResultModel {
	/** First-try answers as a whole percentage. */
	acc: number;
	/** A checkpoint not passed: the screen must not say "well done" beside "not yet". */
	failed: boolean;
	/** The Armenian headline. */
	cheer: string;
	blurb: UiKey;
	/** A checkpoint's pass or fail line; null for any other lesson. */
	verdict: { passed: boolean; key: UiKey } | null;
	/**
	 * A checkpoint's misses, made actionable (partner decision: not feedback
	 * every lesson needs). `keys` is exactly what "Review these" reviews.
	 */
	review: { ids: string[]; keys: ProgressKey[] } | null;
	/** Units this lesson unlocked. */
	unlocked: string[];
	treeCue: Extract<CueKind, "bloom" | "ripen"> | null;
}

/** The part of the model that is about the lesson, not the tree. */
function lessonFacts(res: LessonSummary): Omit<ResultModel, "unlocked" | "treeCue"> {
	const failed = res.passed === false;
	// A review of given keys has no unit, and so no verdict.
	const passed = res.unit ? res.passed : null;
	return {
		acc: res.graded ? Math.round((res.firstTry / res.graded) * 100) : 100,
		failed,
		cheer: failed ? "Շարունակի՛ր!" : "Ապրե՛ս!",
		blurb: failed ? "result.blurbNotYet" : "result.blurb",
		verdict:
			passed === null
				? null
				: { passed, key: passed ? "result.checkpointPassed" : "result.checkpointFailed" },
		review:
			passed !== null && res.missed.length > 0 ? { ids: res.missed, keys: res.missedKeys } : null,
	};
}

export function resultModel(res: LessonSummary, moment: TreeMoment): ResultModel {
	return {
		...lessonFacts(res),
		unlocked: res.newly,
		treeCue: moment.recovered ? "bloom" : moment.ripened.length ? "ripen" : null,
	};
}

export interface LessonStep1 {
	mood: "cheer" | "miss" | "twirl";
	cheer: string;
	acc: number;
	perfect: boolean;
	/** A flawless lesson earns its line and the duduk's twirl. */
	perfectLine: boolean;
	/** Null under 3 graded answers: a run of one or two is not worth a tile. */
	bestRun: number | null;
	durationMs: number;
	/** "m:ss", capped at "59:59". */
	time: string;
	pocket: { kind: "letters"; n: number; of: number } | { kind: "words"; n: number; added: number };
	verdict: ResultModel["verdict"];
	review: ResultModel["review"];
}

const MAX_SHOWN_MS = (59 * 60 + 59) * 1000;

export function formatTime(ms: number): string {
	const s = Math.floor(Math.min(Math.max(ms, 0), MAX_SHOWN_MS) / 1000);
	return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

const isLetter = (id: string): boolean => itemById(id)?.kind === "letter";

const distinctItems = (st: GameState): Set<string> => new Set(trainedKeys(st).map(itemIdOf));

export function lessonStep(
	res: LessonSummary,
	stats: { bestRun: number; durationMs: number },
	before: GameState,
	after: GameState,
): LessonStep1 {
	const m = lessonFacts(res);
	const track = res.unit?.track;
	const trained = distinctItems(after);
	let pocket: LessonStep1["pocket"];
	if (track === "lower" || track === "caps") {
		const taught = new Set(
			units
				.filter((u) => u.track === track)
				.flatMap((u) => unitKeys(u).map(itemIdOf))
				.filter(isLetter),
		);
		pocket = {
			kind: "letters",
			n: [...trained].filter((id) => taught.has(id)).length,
			of: taught.size,
		};
	} else {
		const words = (set: Set<string>): number => [...set].filter((id) => !isLetter(id)).length;
		const n = words(trained);
		pocket = { kind: "words", n, added: n - words(distinctItems(before)) };
	}
	const perfect = res.graded > 0 && res.firstTry === res.graded;
	const perfectLine = perfect && !m.failed;
	return {
		mood: m.failed ? "miss" : perfectLine ? "twirl" : "cheer",
		cheer: m.cheer,
		acc: m.acc,
		perfect,
		perfectLine,
		bestRun: res.graded >= 3 ? stats.bestRun : null,
		durationMs: stats.durationMs,
		time: formatTime(stats.durationMs),
		pocket,
		verdict: m.verdict,
		review: m.review,
	};
}

/** The tree is a different size or stage than before the lesson. */
export const treeGrew = (moment: TreeMoment): boolean =>
	moment.from.stage !== moment.to.stage || moment.from.growth !== moment.to.growth;

/** Step 2 is skipped when the tree did not change at all. */
export function treeStepNeeded(m: ResultModel, moment: TreeMoment, celebrating: boolean): boolean {
	return (
		treeGrew(moment) ||
		moment.ripened.length > 0 ||
		moment.recovered ||
		m.unlocked.length > 0 ||
		celebrating
	);
}

/** The tree step's headline: its news, or the blurb when nothing changed. */
export function treeHeadline(moment: TreeMoment, blurb: UiKey): UiKey {
	if (treeGrew(moment)) return "result.treeGrew";
	// Same order as resultModel's treeCue: the bloom is the cue that sounds when both happened.
	if (moment.recovered) return "result.treeBloomed";
	if (moment.ripened.length > 0) return "result.treeRipened";
	return blurb;
}
